import React from "react";

import AlertModal from "@/components/shared/AlertModal";

/**
 * The confirmation prompt for a switch that takes something away.
 *
 * One state object drives it — `null` when nothing is pending:
 *
 *     setConfirmation({
 *       title: 'Turn off "Prompt-safety screening"?',
 *       lines: ["What stops happening.", "What the reader is accepting."],
 *       actionText: "Turn off",
 *       onConfirm: () => applyToggle(row, false),
 *     })
 *
 * The app's own modal, never `window.confirm()`: a native dialog renders a
 * multi-line warning badly, some browsers suppress it outright, and a browser
 * driver dismisses it by default — which silently cancelled every toggle under
 * test and made a gated action look ungated.
 *
 * Each line is its own block, because these warnings are two or three
 * sentences and a single paragraph of them is not read.
 *
 * @param {{title: string, lines: string[], actionText?: string, onConfirm?: Function}|null} confirmation
 * @param {Function} onClose  clears the pending confirmation
 */
export const ConfirmActionModal = ({
  confirmation,
  onClose,
  actionText = "Continue",
  cancelText = "Cancel",
  actionClassName = "bg-red-600 hover:bg-red-700 text-white",
}) => (
  <AlertModal
    open={!!confirmation}
    onOpenChange={(open) => !open && onClose()}
    title={confirmation?.title || ""}
    description={
      <>
        {(confirmation?.lines || []).filter(Boolean).map((line, index) => (
          <span key={index} className="mb-2 block last:mb-0">
            {line}
          </span>
        ))}
      </>
    }
    cancelText={cancelText}
    actionText={confirmation?.actionText || actionText}
    onCancel={onClose}
    onAction={() => {
      const pending = confirmation;
      onClose();
      pending?.onConfirm?.();
    }}
    actionClassName={actionClassName}
  />
);

export default ConfirmActionModal;
